import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { reportsApi } from '@/api/endpoints/reports';
import { translateAttribute, formatAttributeValue } from '@/lib/traccarLabels';

interface Opt {
  id: number;
  name: string;
}
type Cat = 'evento' | 'entrada' | 'saida' | 'io' | 'iot';
interface Row {
  key: string;
  label: string;
  value: string;
  raw: string;
  cat: Cat;
}

const CAT_META: Record<Cat, { label: string; color: string }> = {
  evento: { label: 'Eventos / Alarmes', color: 'text-status-blocked' },
  entrada: { label: 'Entradas', color: 'text-primary' },
  saida: { label: 'Saídas', color: 'text-status-ready' },
  io: { label: 'IOs (CAN/OBD)', color: 'text-brand' },
  iot: { label: 'IoT / Sensores', color: 'text-status-running' },
};

function categorize(key: string): Cat {
  const k = key.toLowerCase();
  if (['alarm', 'alarmbits', 'event', 'eventcode', 'result'].includes(k)) return 'evento';
  if (/^(in|di)\d+$/.test(k) || k === 'input' || k === 'inputs') return 'entrada';
  if (/^(out|do)\d+$/.test(k) || k === 'output' || k === 'outputs') return 'saida';
  if (/^io\d+$/.test(k) || /^adc\d+$/.test(k)) return 'io';
  return 'iot';
}

/**
 * Lê as últimas posições de um veículo, une e traduz os atributos, e deixa
 * escolher em checkboxes o que alimentar o filtro de alarmes da regra. Para
 * alarme/evento usa o CÓDIGO CRU (o que o equipamento manda), não o rótulo.
 */
export function DeviceAttributeDiscovery({ devices, onAddAlarms }: { devices: Opt[]; onAddAlarms: (codes: string[]) => void }) {
  const [deviceId, setDeviceId] = useState<number | ''>('');
  const [rows, setRows] = useState<Row[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const fetchWindow = async (id: number, hours: number) => {
    const to = new Date();
    const from = new Date(to.getTime() - hours * 3600 * 1000);
    return reportsApi.route({ deviceId: id, from: from.toISOString(), to: to.toISOString() });
  };

  const read = async () => {
    if (!deviceId) return;
    setLoading(true);
    setErr(null);
    setInfo(null);
    setChecked(new Set());
    try {
      let positions = await fetchWindow(deviceId, 24);
      if (positions.length < 5) {
        const wider = await fetchWindow(deviceId, 24 * 30);
        if (wider.length > positions.length) positions = wider;
      }
      const last5 = positions.slice(-5);
      if (last5.length === 0) {
        setRows([]);
        setInfo('Sem posições recentes para este veículo.');
        return;
      }
      const merged = new Map<string, unknown>();
      for (const p of last5) for (const [k, v] of Object.entries(p.attributes ?? {})) merged.set(k, v);
      const built: Row[] = Array.from(merged.entries())
        .map(([key, value]) => ({
          key,
          label: translateAttribute(key),
          value: formatAttributeValue(key, value),
          raw: String(value ?? ''),
          cat: categorize(key),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
      setRows(built);
      setInfo(`${last5.length} posição(ões) lida(s), ${built.length} atributos.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Falha ao ler posições.');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (key: string) =>
    setChecked((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const addSelected = () => {
    const codes: string[] = [];
    for (const r of rows) {
      if (!checked.has(r.key)) continue;
      if (r.cat === 'evento' && (r.key === 'alarm' || r.key === 'event')) codes.push(r.raw);
      else codes.push(r.key);
    }
    const clean = Array.from(new Set(codes.map((c) => c.trim()).filter(Boolean)));
    if (clean.length) onAddAlarms(clean);
    setChecked(new Set());
  };

  const byCat = useMemo(
    () =>
      (['evento', 'entrada', 'saida', 'io', 'iot'] as Cat[])
        .map((c) => [c, rows.filter((r) => r.cat === c)] as const)
        .filter(([, r]) => r.length > 0),
    [rows],
  );

  return (
    <div className="space-y-2 rounded-md border border-dashed border-border p-3">
      <p className="text-xs font-semibold text-muted-foreground">Descobrir do veículo</p>
      <div className="flex gap-2">
        <select
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value ? Number(e.target.value) : '')}
          className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">— escolher veículo —</option>
          {devices.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <Button type="button" variant="outline" size="sm" disabled={!deviceId || loading} onClick={() => void read()}>
          {loading ? 'Lendo…' : 'Ler posições'}
        </Button>
      </div>
      {err && <p className="text-[11px] text-status-blocked">{err}</p>}
      {info && <p className="text-[11px] text-muted-foreground">{info}</p>}
      {byCat.length > 0 && (
        <div className="max-h-52 space-y-2 overflow-y-auto">
          {byCat.map(([cat, rs]) => (
            <div key={cat}>
              <p className={`text-[11px] font-bold ${CAT_META[cat].color}`}>{CAT_META[cat].label}</p>
              {rs.map((r) => (
                <label key={r.key} className="flex items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-accent">
                  <input type="checkbox" checked={checked.has(r.key)} onChange={() => toggle(r.key)} className="h-3.5 w-3.5 accent-primary" />
                  <span className="font-medium">{r.label}</span>
                  <span className="truncate text-muted-foreground">= {r.value}</span>
                  <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{r.raw}</span>
                </label>
              ))}
            </div>
          ))}
          <Button type="button" size="sm" disabled={checked.size === 0} onClick={addSelected} className="w-full">
            Adicionar {checked.size || ''} ao filtro de alarmes
          </Button>
        </div>
      )}
    </div>
  );
}
